import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import "./Navbar.css";

const isFutureEvent = (event) => {
  const raw = event.acf?.event_date;
  if (!raw) return true;

  const normalized = raw.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3");
  const eventDate = new Date(normalized);

  if (isNaN(eventDate)) return true;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return eventDate >= today;
};

// Decode HTML entities returned by WordPress, such as
// &#8220; and &#8221; into their actual characters.
const decodeHtmlEntities = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.documentElement.textContent;
};

function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  const eventsDropdownRef = useRef(null);
  const location = useLocation();
  const isEventsActive = location.pathname.startsWith("/events");

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    fetch("https://beszeljegyesulet.hu/wp/wp-json/wp/v2/events?per_page=20")
      .then((res) => res.json())
      .then((data) => setUpcomingEvents(data.filter(isFutureEvent).slice(0, 6)))
      .catch((err) => console.error("Navbar events error:", err));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        eventsDropdownRef.current &&
        !eventsDropdownRef.current.contains(event.target)
      ) {
        setEventsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToTop = () => {
    const scrollElement =
      document.scrollingElement || document.documentElement;

    if (scrollElement) scrollElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;

    window.scrollTo(0, 0);
  };

  const handleNavClick = () => {
    closeMenu();
    setEventsOpen(false);
    scrollToTop();
  };

  return (
    <>
      <nav className="navbar">
        <div className="nav-inner">
          <div className="navbar-left">
            <Link to="/" className="logo-link" onClick={handleNavClick}>
              <img
                src={require("./assets/beszELJ_logo2.png")}
                alt="BeszÉLJ logó"
                className="logo"
              />
            </Link>

            <div className="desktop-links">
              <NavLink
                to="/"
                exact
                activeClassName="active"
                onClick={handleNavClick}
                data-text="Otthon"
              >
                Otthon
              </NavLink>

              <NavLink
                to="/about"
                activeClassName="active"
                onClick={handleNavClick}
                data-text="Rólunk"
              >
                Rólunk
              </NavLink>

              <div
                className={`desktop-events-dropdown ${
                  eventsOpen ? "open" : ""
                } ${isEventsActive ? "events-active" : ""}`}
                ref={eventsDropdownRef}
                onMouseEnter={() => setEventsOpen(true)}
                onMouseLeave={() => setEventsOpen(false)}
              >
                <button
                  type="button"
                  className="desktop-events-trigger"
                  data-text="Események"
                >
                  <NavLink
                    to="/events"
                    activeClassName="active"
                    onClick={handleNavClick}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    Események
                  </NavLink>

                  <span className="desktop-events-arrow"></span>
                </button>

                <div className="desktop-events-menu">
                  <div className="desktop-events-menu-inner">
                    <span className="desktop-events-title">
                      Közelgő események
                    </span>

                    <div className="desktop-events-list">
                      <Link
                        to="/events"
                        className="desktop-events-item desktop-events-all"
                        onClick={handleNavClick}
                      >
                        Összes esemény
                      </Link>

                      {upcomingEvents.length === 0 ? (
                        <span
                          className="desktop-events-item"
                          style={{ color: "#888" }}
                        >
                          Nincs közelgő esemény
                        </span>
                      ) : (
                        upcomingEvents.map((event) => (
                          <Link
                            key={event.id}
                            to={`/events/${event.slug}`}
                            className="desktop-events-item"
                            onClick={handleNavClick}
                          >
                            {decodeHtmlEntities(event.title.rendered)}
                          </Link>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <NavLink
                to="/blog"
                activeClassName="active"
                onClick={handleNavClick}
                data-text="Blog"
              >
                Blog
              </NavLink>

              <NavLink
                to="/stuttering"
                activeClassName="active"
                onClick={handleNavClick}
                data-text="A dadogásról"
              >
                A dadogásról
              </NavLink>

              <NavLink
                to="/faq"
                activeClassName="active"
                onClick={handleNavClick}
                data-text="Kérdések"
              >
                Kérdések
              </NavLink>
            </div>
          </div>

          <div className="navbar-right">
            <Link
              to="/membership"
              className="btn-primary"
              onClick={handleNavClick}
            >
              Legyél tagunk
            </Link>

            <span className="desktop-divider"></span>

            <NavLink
              to="/support"
              className="btn-support"
              activeClassName="active"
              onClick={handleNavClick}
            >
              Támogass minket
            </NavLink>

            <button
              type="button"
              aria-label="Menü"
              className={`hamburger ${menuOpen ? "active" : ""}`}
              onClick={toggleMenu}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </nav>

      <div className="mobile-menu-wrapper">
        <div className={`mobile-menu ${menuOpen ? "open" : ""}`}>
          <div className="mobile-buttons">
            <Link
              to="/membership"
              className="btn-primary"
              onClick={handleNavClick}
            >
              Legyél tagunk
            </Link>

            <span className="mobile-divider"></span>

            <Link
              to="/support"
              className="btn-support"
              onClick={handleNavClick}
            >
              Támogass minket
            </Link>
          </div>

          <nav className="mobile-links">
            <NavLink
              to="/"
              exact
              activeClassName="active"
              onClick={handleNavClick}
            >
              Otthon
            </NavLink>

            <NavLink
              to="/about"
              activeClassName="active"
              onClick={handleNavClick}
            >
              Rólunk
            </NavLink>

            <NavLink
              to="/events"
              activeClassName="active"
              onClick={handleNavClick}
            >
              Események
            </NavLink>

            <NavLink
              to="/blog"
              activeClassName="active"
              onClick={handleNavClick}
            >
              Blog
            </NavLink>

            <NavLink
              to="/stuttering"
              activeClassName="active"
              onClick={handleNavClick}
            >
              A dadogásról
            </NavLink>

            <NavLink
              to="/faq"
              activeClassName="active"
              onClick={handleNavClick}
            >
              Kérdések
            </NavLink>
          </nav>
        </div>
      </div>
    </>
  );
}

export default Navbar;